import {Component, Input, OnInit} from '@angular/core';
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {NgbActiveModal, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ToastrService} from "ngx-toastr";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {UserLicensesService} from "@shared/api/services/v2/user-licenses.service";
import {UserLicenseHistoryEntry} from "@shared/api/models/query/user-license-history.model";
import {LicenseDeactivateModal} from "./license-deactivate.modal";

/**
 * UNIVAPLAT-2774: admin-dashboard modal showing a user's full license history
 * (`GET /api/v2/admin/users/{userId}/licenses` via
 * {@link UserLicensesService.listHistory}, newest-first per backend contract
 * UNIVAPLAT-2756).
 *
 * Each DEACTIVATED period row carries an admin "Edit" action (UNIVAPLAT-2757
 * acceptance criteria — review pass 1): it opens {@link LicenseDeactivateModal}
 * in `editReason` mode with the row's explicit `licenseId` + current reason, so
 * ANY past period's reason can be edited from the history list — not just the
 * newest one. When the edit modal closes with `true`, the history reloads so the
 * updated reason is reflected in the list, and the mutation is remembered.
 *
 * The Close button propagates `close(mutated)` — `true` if any reason edit
 * happened while the modal was open — so the dashboard page can refresh its
 * rows and keep them consistent with the history.
 *
 * Four UI states (acceptance criteria):
 * - loading: spinner while the request is in flight
 * - empty:   "No license history" message when the history is an empty list
 * - error:   toastr with the backend's verbatim message (backend owns the
 *            user-facing error text, e.g. 404 unknown user) + inline message;
 *            the modal stays open so the admin can close it deliberately
 * - success: populated table — license name, active/deactivated status badge,
 *            deactivation reason, expiredAt, createdAt, edit action
 *
 * Dates render via Angular's DatePipe (no moment/dayjs — per the Sub-task
 * design note). An entry with `expiredAt === null` is the ACTIVE period.
 *
 * No Figma frame attached — styled consistently with the sibling
 * LicenseDeactivateModal (UNIVAPLAT-2772); reassess once a frame is provided.
 */
@Component({
    selector: 'cdss-license-history-modal',
    standalone: true,
    imports: [
        NgIf,
        NgForOf,
        DatePipe,
        SpinnerComponent
    ],
    templateUrl: './license-history.modal.html'
})
export class LicenseHistoryModal implements OnInit {

    @Input() userId: number;
    /** Display-only context in the modal header; may arrive masked (PHI masking). */
    @Input() userName = '';

    loading = true;
    error = false;
    entries: UserLicenseHistoryEntry[] = [];
    /** True once any deactivation reason was edited — propagated via close(mutated). */
    mutated = false;

    constructor(
        public modal: NgbActiveModal,
        private modalService: NgbModal,
        private userLicensesService: UserLicensesService,
        private toastr: ToastrService,
    ) {
    }

    async ngOnInit() {
        await this.load();
    }

    /**
     * UNIVAPLAT-2757 (review pass 1): edits the deactivation reason of THIS
     * row's license period — the explicit `licenseId` skips the edit modal's
     * newest-period resolution, and the reason is pre-filled from the entry
     * already loaded here (no extra history request). On close(true) the list
     * reloads so the updated reason is reflected.
     */
    openEditReason(entry: UserLicenseHistoryEntry): void {
        const ref = this.modalService.open(LicenseDeactivateModal, {centered: true});
        ref.componentInstance.mode = 'editReason';
        ref.componentInstance.userId = this.userId;
        ref.componentInstance.userName = this.userName;
        ref.componentInstance.licenseId = entry.id;
        ref.componentInstance.initialReason = entry.deactivationReason;
        ref.result.then(async (changed: boolean) => {
            if (changed) {
                this.mutated = true;
                await this.load();
            }
        }).catch(() => {/* dismissed */});
    }

    private async load(): Promise<void> {
        this.loading = true;
        this.error = false;
        try {
            this.entries = await this.userLicensesService.listHistory(this.userId);
        } catch (error: any) {
            // Backend owns the user-facing error text (404 unknown user) —
            // surface it verbatim, plus an inline message in the modal body.
            this.error = true;
            this.toastr.error(error?.error?.error);
        } finally {
            this.loading = false;
        }
    }
}
